import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import LibraryMusicOutlinedIcon from '@mui/icons-material/LibraryMusicOutlined';
import { AppBar, Box, Button, IconButton, Stack, Toolbar, useMediaQuery, useTheme } from '@mui/material';
import { AccountMenu } from 'components/account/AccountMenu';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { isElectron } from 'utils/isElectron';
import { openExternal } from 'utils/openExternal';
import { DownloadClientButton } from './DownloadButton';
import { NavButton } from './NavButton';
import { useTranslation } from 'react-i18next'

export function Nav() {
  const { t } = useTranslation()
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [canGoBack, setCanGoBack] = useState(false);
  const [canGoForward, setCanGoForward] = useState(false);

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isVerySmallScreen = useMediaQuery('(max-width:450px)');
  const isExtremelySmallScreen = useMediaQuery('(max-width:300px)');

  useEffect(() => {
    const checkHistory = () => {
      setCanGoBack(window.history.state && window.history.state.idx > 0);
      setCanGoForward(window.history.state && window.history.state.idx < window.history.length - 1);
    };

    checkHistory();

    window.addEventListener('popstate', checkHistory);

    return () => {
      window.removeEventListener('popstate', checkHistory);
    };
  });

  const handleBackClick = () => {
    if (canGoBack) {
      navigate(-1);
    }
  };

  const handleForwardClick = () => {
    if (canGoForward) {
      navigate(1);
    }
  };

  const isActive = (path: string) => location.pathname.startsWith(path);

  return (
    <AppBar
      position="static"
      variant="elevation"
      elevation={2}
      sx={{
        background:
          theme.palette.mode == 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
        px: 0,
        zIndex: 2,
      }}
    >
      <Toolbar disableGutters sx={{ mx: isExtremelySmallScreen ? 0.5 : (isVerySmallScreen ? 1 : 2), flexWrap: 'nowrap' }}>
        {isElectron() && !isExtremelySmallScreen && (
          <Stack direction="row" sx={{ mr: isVerySmallScreen ? 0.5 : 2 }}>
            <IconButton onClick={handleBackClick} disabled={!canGoBack} size={isVerySmallScreen ? 'small' : 'medium'}>
              <ArrowBackOutlinedIcon fontSize={isVerySmallScreen ? 'small' : 'medium'} />
            </IconButton>
            <IconButton onClick={handleForwardClick} disabled={!canGoForward} size={isVerySmallScreen ? 'small' : 'medium'}>
              <ArrowForwardOutlinedIcon fontSize={isVerySmallScreen ? 'small' : 'medium'} />
            </IconButton>
          </Stack>
        )}
        <Stack direction="row" spacing={isExtremelySmallScreen ? 0 : (isVerySmallScreen ? 0.5 : 2)}>
          <NavButton component={Link} to="/events" startIcon={<CalendarMonthOutlinedIcon />} sx={{ minWidth: isExtremelySmallScreen ? 40 : 'auto' }}>
            {!isVerySmallScreen && t('events', 'Events')}
          </NavButton>
          <NavButton component={Link} to="/musicLibrary" startIcon={<LibraryMusicOutlinedIcon />} sx={{ minWidth: isExtremelySmallScreen ? 40 : 'auto' }}>
            {!isVerySmallScreen && t('library', 'Library')}
          </NavButton>
        </Stack>
        <Stack direction="row" sx={{ ml: 'auto', alignItems: 'center' }}>
          {!isExtremelySmallScreen && (
            <Button
              startIcon={<FeedbackOutlinedIcon />}
              onClick={() => openExternal('https://dj-play.it/feedback')}
              sx={{ mr: isVerySmallScreen ? 1 : 3 }}
              variant="outlined"
              size={isVerySmallScreen ? 'small' : 'medium'}
            >
              {!isVerySmallScreen && t('giveFeedback', 'Give Feedback')}
            </Button>
          )}
          {!isElectron() && !isVerySmallScreen && (
            <Box sx={{ mr: isVerySmallScreen ? 1 : 3, display: 'inline-block' }}>
              <DownloadClientButton />
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AccountMenu />
          </Box>
        </Stack>
      </Toolbar>
    </AppBar>
  );
}
